import Icon from '@/components/ui/AppIcon';

interface Invoice {
  id: string;
  date: string;
  amount: string;
  status: 'paid' | 'pending' | 'overdue';
  dueDate: string;
  description: string;
}

export default function BillingOverview() {
  const invoices: Invoice[] = [
    {
      id: "INV-2026-001",
      date: "Jan 1, 2026",
      amount: "$4,850.00",
      status: "paid",
      dueDate: "Jan 15, 2026",
      description: "Monthly Managed Services - January 2026"
    },
    {
      id: "INV-2025-012",
      date: "Dec 1, 2025",
      amount: "$4,850.00",
      status: "paid",
      dueDate: "Dec 15, 2025",
      description: "Monthly Managed Services - December 2025"
    },
    {
      id: "INV-2026-002",
      date: "Feb 1, 2026",
      amount: "$4,850.00",
      status: "pending",
      dueDate: "Feb 15, 2026",
      description: "Monthly Managed Services - February 2026"
    }
  ];

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'overdue':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusText = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      case 'overdue':
        return 'Overdue';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-muted/30">
        <h2 className="text-xl font-headline font-bold text-primary">Billing & Invoices</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your payments and invoices</p>
      </div>
      
      <div className="p-6 border-b border-border bg-brand-primary/5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-sm font-body font-semibold text-muted-foreground mb-1">
              Current Balance
            </p>
            <p className="text-3xl font-headline font-bold text-primary">
              $4,850.00
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Due on February 15, 2026
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-6 py-3 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center gap-2">
              <Icon name="CreditCardIcon" size={18} variant="outline" />
              <span>Pay Now</span>
            </button>
            <button className="px-6 py-3 bg-card hover:bg-muted border border-border text-primary rounded-lg font-cta font-semibold text-sm transition-colors duration-200 flex items-center gap-2">
              <Icon name="ArrowDownTrayIcon" size={18} variant="outline" />
              <span>Download</span>
            </button>
          </div>
        </div>
      </div>
      
      <div className="divide-y divide-border">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="p-6 hover:bg-muted/30 transition-colors duration-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-accent font-semibold text-muted-foreground">
                    {invoice.id}
                  </span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getStatusColor(invoice.status)}`}>
                    {getStatusText(invoice.status)}
                  </span>
                </div>
                <h3 className="text-base font-headline font-semibold text-primary mb-2">
                  {invoice.description}
                </h3>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={14} variant="outline" />
                    <span>Issued: {invoice.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={14} variant="outline" />
                    <span>Due: {invoice.dueDate}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <p className="text-2xl font-headline font-bold text-primary">
                  {invoice.amount}
                </p>
                <button className="px-4 py-2 text-sm font-cta font-semibold text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors duration-200 flex items-center gap-2">
                  <Icon name="DocumentTextIcon" size={16} variant="outline" />
                  <span>View</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="px-6 py-4 border-t border-border bg-muted/30">
        <button className="text-sm font-cta font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors duration-200 flex items-center gap-2">
          <span>View All Invoices</span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}